// state.js — the app's in-memory state and the pure helpers that operate on it.
// Every other module reads and writes through the shared `S` object.

import { normalizeTask, normalizeCompleted, legacyId } from '../model.js';

export const S = {
  tasks: [],            // open tasks (random pool)
  completed: [],        // completed records (recent window for signed-in users)
  tags: [],
  manualQueue: [],      // FIFO for sequential ("Randomize off") tasks
  deletedIds: [],       // tombstones: ids deleted on this account (see model.rememberDeleted)
  currentFishId: null,  // id of the task being worked on
  currentFishStartTime: null, // Date when it was drawn
  currentAddTag: '',    // tag applied to new tasks; also narrows the random pool (by design)
  cloudTotals: null,    // { count, seconds } — all-time completed totals from meta (db.js)
  historyWindowDays: 0, // >0 when `completed` only holds recent history
  streak: 0,
  lastCatchDate: ''
};

export const placeholderArt = `   ><(((º>              ><>            ><((*>                    ><>            ><(((º>

            ><>           ><((('>       ><°)))'>         ><>          ><((º>

><{{{*}>                 ><>       ><^)))°>              ><((('>          ><>`;

export function newId() {
  return (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : 'id_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

// Give every item in a list an id (migration for data created before ids existed).
// provisional = true gives id-less records a deterministic legacy_* id instead
// of a fresh UUID. Such ids are not treated as identity (see sameTask), so a
// record from an old, id-less local copy is matched to the cloud by text at
// login instead of being uploaded as a new record.
export function ensureIds(list, provisional = false) {
  if (!Array.isArray(list)) return [];
  list.forEach((item, i) => {
    if (item && typeof item === 'object' && !item.id) item.id = provisional ? legacyId('local', item, i) : newId();
  });
  return list;
}

// Canonical shapes (ISO dates, numeric durations) for lists loaded from localStorage.
export function normalizeLists() {
  S.tasks = S.tasks.map(t => normalizeTask(t)).filter(Boolean);
  S.completed = S.completed.map(c => normalizeCompleted(c)).filter(Boolean);
  S.manualQueue = S.manualQueue.map(q => normalizeTask(q)).filter(Boolean);
}

// Ids of the form legacy_* were derived by migration for records that had no
// id; they are not a real identity, so such records still match by text and
// the local (UUID) copy replaces them.
export function realId(id) {
  return typeof id === 'string' && id.length > 0 && !id.startsWith('legacy_');
}

export function sameTask(a, b) {
  if (a.id && b.id && a.id === b.id) return true;
  if (realId(a.id) && realId(b.id)) return false;
  return a.text === b.text;
}

// Merge this device's records into the cloud's. The cloud copy wins for a
// record present on both sides (another device may have edited it); the
// local copy only lends its id when the cloud record has no real one.
// Records only known locally are added.
export function mergeInto(cloudList, localList, skipIf = () => false) {
  const out = [...cloudList];
  localList.forEach(l => {
    if (skipIf(l)) return;
    const i = out.findIndex(c => sameTask(c, l));
    if (i === -1) out.push(l);
    else if (!realId(out[i].id) && realId(l.id)) out[i] = { ...out[i], id: l.id };
  });
  return out;
}

export function getCurrentTask() {
  return S.currentFishId ? (S.tasks.find(t => t.id === S.currentFishId) || null) : null;
}

export function activeRecord() {
  return S.currentFishId
    ? { id: S.currentFishId, startedAt: S.currentFishStartTime.toISOString() }
    : null;
}

// Everything that is persisted to Firestore, in one place.
export function buildState() {
  return {
    tasks: S.tasks, completed: S.completed, tags: S.tags, manualQueue: S.manualQueue,
    active: activeRecord(),
    streakInfo: { streak: S.streak, lastCatchDate: S.lastCatchDate },
    deletedIds: S.deletedIds
  };
}

// What the Account panel's "Download my data" starts from (db.js adds full history).
export function exportData() {
  return { tasks: S.tasks, completed: S.completed, tags: S.tags, manualQueue: S.manualQueue, streakInfo: { streak: S.streak, lastCatchDate: S.lastCatchDate } };
}

export function labelOf(t) {
  return `${t.tag ? `[${t.tag}] ` : ''}${t.text}`;
}
